import {
  addToaster,
  EVENT_CONSTANTS,
  getLayoutByIdApi,
  LayoutResponseType,
  WorkflowProcessType,
  getAxiosErrorMessage,
  isAxiosConflictError,
  CONFLICT_SAVE_FALLBACK,
  logAxiosError,
} from '@app-store'
import { useEffect, useState } from 'react'
import {
  editEntityData,
  getEntityData,
  getEntityDataByIdEmbedded,
} from '../../apis/entityData.api'
import { FieldValues } from 'react-hook-form'
import { EntityLayout, LayoutData } from '../../types/layout.types'
import FormRenderer from '../FormRenderer'
import { CustomButton } from '@ui-kit'
import { MODE_CONSTANTS, NAMESPACES } from '../../utils/constants'
import { isEmpty } from 'lodash'
import {
  useAppSelector,
  RootState,
  useAppDispatch,
} from '../../utils/agent.store'
import {
  fetchPatientCorrespondingToCaseId,
  permissionObject,
} from '../../utils/helperFunctions'
import { isReadOnlyUser, isSuperAdmin } from '../../utils/permission'
import { useParams } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import { TOASTER_NAMESPACE } from '../../constants/addEditPage.strings'

const SubStickySummary = ({
  recordId,
  workflowDefinition,
  isDisable,
}: {
  recordId: string
  workflowDefinition: WorkflowProcessType
  isDisable?: boolean
}) => {
  const entityId =
    workflowDefinition?.global_variables?.sub_sticky_summary?.entity_id
  const layoutId =
    workflowDefinition?.global_variables?.sub_sticky_summary?.layout_id
  const [layout, setLayout] = useState<LayoutResponseType>()
  const [layoutData, setLayoutData] = useState<FieldValues>()
  const [mode, setMode] = useState<'add' | 'edit' | 'view'>(
    MODE_CONSTANTS?.VIEW
  )
  const [isEditButtonVisible, setIsEditButtonVisible] = useState<
    boolean | undefined
  >()
  const [isSaving, setIsSaving] = useState(false)
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions.programPermissions
  )
  const caseData = useAppSelector(
    (state: RootState) => state.workflowData?.caseData
  )
  const { caseId } = useParams()
  const dispatch = useAppDispatch()

  const fetchLayout = async () => {
    try {
      const response = await getLayoutByIdApi({
        entityId: entityId,
        layoutId: layoutId,
      })
      if (response.data) {
        setLayout(response?.data)
        await fetchEntityData()
      }
    } catch (err) {
      console.error(err)
    }
  }

  const fetchEntityData = async () => {
    try {
      const summaryId = await fetchSummaryId()
      const response = await getEntityData(
        'case_summary',
        Number(entityId),
        Number(layoutId),
        summaryId
      )
      if (response.data) {
        setLayoutData(response?.data)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const saveEntityRecord = async (data: FieldValues) => {
    if (isSaving) {
      return
    }
    setIsSaving(true)
    try {
      if (
        data?.cases?.patient?.patient_journey_status?.id ||
        data?.cases?.patient?.patient_journey_status_reason?.id
      ) {
        const patientResponse = await fetchPatientCorrespondingToCaseId(
          caseId ?? ''
        )
        if (!patientResponse?.patient?.patient_key) {
          dispatch(
            addToaster({
              message: TOASTER_NAMESPACE.NO_PATIENT_FOUND,
              type: 'error',
              id: uuid(),
            })
          )
          return
        }
      }
      if (layout && mode !== MODE_CONSTANTS?.VIEW) {
        if (isEmpty(layoutData)) {
          const res = await editEntityData(
            workflowDefinition?.global_variables?.entity_name ?? '',
            Number(workflowDefinition?.global_variables?.entity_id),
            Number(
              workflowDefinition?.global_variables?.left_split?.edit_layout_id
            ),
            {
              case_summary: data,
            },
            recordId
          )
          if (res.data) {
            setMode(MODE_CONSTANTS?.VIEW)
          }
        } else {
          const res = await editEntityData(
            layout?.entity_name,
            layout.entity_id,
            Number(layout?.id),
            data,
            layoutData?.id
          )
          if (res?.data) {
            setMode(MODE_CONSTANTS?.VIEW)
          }
        }
      }
    } catch (err) {
      logAxiosError(err, 'SubStickySummary saveEntityRecord')
      dispatch(
        addToaster({
          message: isAxiosConflictError(err)
            ? getAxiosErrorMessage(err, CONFLICT_SAVE_FALLBACK)
            : TOASTER_NAMESPACE.FAILED_TO_ADD_RECORD,
          type: 'error',
          id: uuid(),
        })
      )
    } finally {
      setIsSaving(false)
    }
  }

  const fetchSummaryId = async () => {
    try {
      const res = await getEntityDataByIdEmbedded(
        workflowDefinition?.global_variables?.entity_name ?? '',
        Number(workflowDefinition?.global_variables?.entity_id),
        ['case_summary'], // to improve
        recordId
      )
      if (res?.data) {
        return res?.data?.case_summary?.id
      }
    } catch (err) {
      console.error(err)
    }
  }

  const editButtonVisible = () => {
    if (caseData) {
      const hasPermission = permissionObject.Queue_Review_Manage(
        programPermissions,
        caseData.programs?.id?.toString() ?? '',
        caseData.services?.service_name?.toString() ?? ''
      )
      setIsEditButtonVisible(hasPermission)
    } else {
      setIsEditButtonVisible(true)
    }
  }

  useEffect(() => {
    fetchSummaryId()
    fetchLayout()
  }, [entityId, layoutId, workflowDefinition, mode])

  // TODO: Can remove now
  useEffect(() => {
    window.addEventListener(
      EVENT_CONSTANTS?.TASK_COMPLETE_REFETCH_SUMMARY,
      fetchEntityData
    )
    return () => {
      window.removeEventListener(
        EVENT_CONSTANTS?.TASK_COMPLETE_REFETCH_SUMMARY,
        fetchEntityData
      )
    }
  }, [])

  useEffect(() => {
    editButtonVisible()
  }, [programPermissions, caseData])

  useEffect(() => {
    if (isDisable) {
      setMode(MODE_CONSTANTS?.VIEW)
    }
  }, [isDisable])

  return (
    <div className="w-full">
      <FormRenderer
        key={mode}
        heading={
          <div className="flex justify-between py-1 items-center">
            <div className="font-primary text-base">{NAMESPACES.HEADER}</div>
            {!isReadOnlyUser() ? (
              isEditButtonVisible || isSuperAdmin() ? (
                <div className="flex items-center">
                  {mode === MODE_CONSTANTS?.EDIT ||
                  mode === MODE_CONSTANTS?.ADD ? (
                    <div className="flex gap-3">
                      <CustomButton
                        type="button"
                        className="bg-white ml-2"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setMode(MODE_CONSTANTS?.VIEW)
                        }}
                      >
                        {NAMESPACES?.DISCARD_CHNAGES}
                      </CustomButton>
                      <CustomButton
                        type="submit"
                        isFilled
                        onClick={(e) => {
                          e.stopPropagation()
                        }}
                        disabled={isDisable || isSaving}
                      >
                        {mode === MODE_CONSTANTS?.ADD
                          ? NAMESPACES?.SUBMIT
                          : NAMESPACES?.UPDATE_BUTTON}
                      </CustomButton>
                    </div>
                  ) : (
                    <CustomButton
                      type="button"
                      className="bg-white"
                      disabled={isDisable}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setMode(MODE_CONSTANTS?.EDIT)
                      }}
                    >
                      {NAMESPACES?.EDIT_BUTTON}
                    </CustomButton>
                  )}
                </div>
              ) : null
            ) : null}
          </div>
        }
        layout={layout?.layout_data as LayoutData}
        mode={mode}
        handleFormSubmit={(e) => {
          saveEntityRecord(e)
        }}
        data={layoutData}
        fullLayout={layout as EntityLayout}
        footer={undefined}
      />
    </div>
  )
}

export default SubStickySummary
